import { useEffect, useState } from 'react';
import { apiCall, default as API_CONFIG } from '../config/api';

/**
 * The default Settings shape, mirroring the backend UserSettings record.
 * Used as the initial state until the GET resolves and as a fallback if
 * the user hasn't saved anything yet (the backend also returns defaults
 * on first read, but having them frontend-side avoids a layout flash).
 */
export const DEFAULT_SETTINGS = {
  emailAddress: '',
  emailTime: '07:00',
  emailEnabled: true,
  autoSync: true,
  syncTime: '16:30',
  // AI model preferences. defaultModel '' = no preference (each task keeps its
  // built-in default). modelOverrides maps a task key -> model id; '' inherits.
  defaultModel: '',
  modelOverrides: {},
  // Per-kind notification switches: { 'daily-report': { push: false } }.
  // Missing means on. See PushNotifications.js.
  notifications: {},
};

/**
 * Loads the user settings on mount and exposes a save function. The hook
 * holds the current draft locally so the form can edit freely without
 * round-tripping; save() POSTs the draft and returns the server's echo.
 *
 * Returned shape:
 *   { settings, setSettings, loading, saving, error, save }
 */
export const useSettings = () => {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    apiCall(API_CONFIG.endpoints.settings)
      .then((data) => {
        if (cancelled) return;
        // Merge over defaults so an older saved doc with deprecated nested
        // fields doesn't crash the form -- those fields are simply dropped.
        setSettings({
          ...DEFAULT_SETTINGS,
          ...(data || {}),
        });
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Failed to load settings', err);
        setError('Failed to load settings');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, []);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const saved = await apiCall(API_CONFIG.endpoints.settings, {
        method: 'POST',
        body: JSON.stringify(settings),
      });
      // Backend stamps updatedAt + canonicalises id/partitionKey; pick that up.
      if (saved) setSettings((prev) => ({ ...prev, ...saved }));
      return saved;
    } catch (err) {
      console.error('Failed to save settings', err);
      setError(err?.message || 'Failed to save settings');
      throw err;
    } finally {
      setSaving(false);
    }
  };

  return { settings, setSettings, loading, saving, error, save };
};
